import type { WorkflowVersionDiff } from '@cortex/types';
import { Fragment, useMemo, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';

export type DiffMode = 'unified' | 'split';

import {
  diffLines,
  nodeSnapshotText,
  toSideBySideRows,
  type DiffLine,
  type DiffRow,
  type DiffSpan,
  type RefResolver,
  type VersionSettingsDiff,
  type VersionSettingsField,
} from '../../lib/workflow-graph-diff';

/**
 * Human labels for the workflow-level fields, using the exact wording the
 * Schema panel puts on each control, so the summary badge's tooltip names them
 * the way the author saw them when editing. The raw field names aren't repeated
 * anywhere else — they're the keys of the rendered document itself.
 */
const settingsFieldLabels: Record<VersionSettingsField, string> = {
  inputSchema: 'Inputs',
  outputSchema: 'Output Validation',
  outputMapping: 'Outputs',
  dedupEnabled: 'Deduplication',
  signalKeyTemplate: 'Signal key template',
};

const successColor = 'var(--status-success, #3fb950)';
const warningColor = 'var(--status-warning, #d29922)';
const dangerColor = 'var(--status-danger, #f85149)';

const containerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--sp-2)',
};

const summaryRowStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'var(--sp-2)',
  alignItems: 'center',
};

const sectionStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--sp-2)',
};

const sectionLabelStyle: CSSProperties = {
  fontSize: 'var(--fs-xs)',
  color: 'var(--text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: 'var(--ls-mono, 0.08em)',
};

const entryStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  padding: 'var(--sp-2)',
  border: '1px solid var(--border-subtle)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--surface-2)',
};

const headerRowStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'baseline',
  gap: 'var(--sp-1)',
  minWidth: 0,
};

const diffBlockBaseStyle: CSSProperties = {
  border: '1px solid var(--border-subtle)',
  borderRadius: 'var(--radius-xs)',
  background: 'var(--surface)',
  fontFamily: 'var(--font-mono, monospace)',
  fontSize: 12,
  lineHeight: 1.5,
  overflow: 'hidden',
};

const diffLineBaseStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 'var(--sp-2)',
  padding: '0 var(--sp-2)',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-all',
};

const diffLinePrefixStyle: CSSProperties = {
  flex: '0 0 auto',
  width: 18,
  textAlign: 'center',
};

const sideBySideCellBaseStyle: CSSProperties = {
  padding: '0 var(--sp-2)',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-all',
  minHeight: '1.5em',
};

const emptyCellStyle: CSSProperties = {
  ...sideBySideCellBaseStyle,
  background:
    'repeating-linear-gradient(-45deg, transparent, transparent 3px, rgba(127,127,127,0.06) 3px, rgba(127,127,127,0.06) 6px)',
};

const changedNodeColorStyle = (kind: DiffLine['kind']): CSSProperties => {
  if (kind === 'add') return { color: successColor, background: 'rgba(63, 185, 80, 0.12)' };
  if (kind === 'del') return { color: dangerColor, background: 'rgba(248, 81, 73, 0.12)' };
  return {};
};

const spanHighlightStyle = (spanKind: DiffSpan['kind']): CSSProperties => {
  if (spanKind === 'del') return { background: 'rgba(248, 81, 73, 0.7)', borderRadius: 2 };
  if (spanKind === 'add') return { background: 'rgba(63, 185, 80, 0.7)', borderRadius: 2 };
  return {};
};

const countBadgeStyle = (color: string): CSSProperties => ({
  color,
  borderColor: color,
  background: 'transparent',
});

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  if (error !== null && typeof error === 'object') {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string') return message;
  }
  return String(error);
}

function hasAnyChanges(
  data: WorkflowVersionDiff,
  settingsDiff: VersionSettingsDiff | null,
): boolean {
  return (
    settingsDiff !== null ||
    data.nodes.added.length > 0 ||
    data.nodes.removed.length > 0 ||
    data.nodes.changed.length > 0 ||
    data.edges.added.length > 0 ||
    data.edges.removed.length > 0 ||
    data.edges.changed.length > 0
  );
}

function linePrefix(kind: DiffLine['kind']): string {
  if (kind === 'add') return '+ ';
  if (kind === 'del') return '− ';
  return '  ';
}

function snapshotLines(text: string, kind: 'add' | 'del'): DiffLine[] {
  return text.split('\n').map((line) => ({ kind, text: line }));
}

function renderLineContent(line: DiffLine) {
  return line.spans
    ? line.spans.map((span, si) => (
        <span key={si} style={spanHighlightStyle(span.kind)}>
          {span.text}
        </span>
      ))
    : line.text;
}

function renderDiffLine(line: DiffLine, index: number) {
  return (
    <div
      key={`${line.kind}-${index}-${line.text}`}
      style={{ ...diffLineBaseStyle, ...changedNodeColorStyle(line.kind) }}
    >
      <span style={diffLinePrefixStyle}>{linePrefix(line.kind)}</span>
      <span>{renderLineContent(line)}</span>
    </div>
  );
}

function renderUnifiedBlock(lines: DiffLine[]) {
  return (
    <div style={{ ...diffBlockBaseStyle, display: 'flex', flexDirection: 'column' }}>
      {lines.map((line, index) => renderDiffLine(line, index))}
    </div>
  );
}

function renderSideBySideCell(line: DiffLine | undefined, isLeft: boolean) {
  const borderRight = isLeft ? { borderRight: '1px solid var(--border-subtle)' } : {};
  if (!line) return <div style={{ ...emptyCellStyle, ...borderRight }} />;
  return (
    <div
      style={{ ...sideBySideCellBaseStyle, ...changedNodeColorStyle(line.kind), ...borderRight }}
    >
      {renderLineContent(line)}
    </div>
  );
}

function renderSideBySideBlock(lines: DiffLine[]) {
  const rows = toSideBySideRows(lines);
  return (
    <div style={{ ...diffBlockBaseStyle, display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
      {rows.map((row: DiffRow, index: number) => (
        <Fragment key={index}>
          {renderSideBySideCell(row.left, true)}
          {renderSideBySideCell(row.right, false)}
        </Fragment>
      ))}
    </div>
  );
}

function renderBlock(lines: DiffLine[], mode: 'unified' | 'split') {
  return mode === 'split' ? renderSideBySideBlock(lines) : renderUnifiedBlock(lines);
}

function summaryBadgeStyle(kind: 'add' | 'changed' | 'del'): CSSProperties {
  if (kind === 'add') return countBadgeStyle(successColor);
  if (kind === 'changed') return countBadgeStyle(warningColor);
  return countBadgeStyle(dangerColor);
}

function nodeCountBadges(data: WorkflowVersionDiff) {
  const badges: Array<{ kind: 'add' | 'changed' | 'del'; text: string }> = [];
  if (data.nodes.added.length > 0)
    badges.push({ kind: 'add', text: `+${data.nodes.added.length} added` });
  if (data.nodes.changed.length > 0)
    badges.push({ kind: 'changed', text: `~${data.nodes.changed.length} changed` });
  if (data.nodes.removed.length > 0)
    badges.push({ kind: 'del', text: `−${data.nodes.removed.length} removed` });
  return badges;
}

function edgeSummaryText(data: WorkflowVersionDiff): string | null {
  const added = data.edges.added.length;
  const removed = data.edges.removed.length;
  const changed = data.edges.changed.length;
  if (added === 0 && removed === 0 && changed === 0) return null;
  return `${added} added / ${removed} removed / ${changed} changed edges`;
}

function SectionLabel({ children }: { children: string }) {
  return (
    <div className="t-mono-label t-xs" style={sectionLabelStyle}>
      {children}
    </div>
  );
}

function DiffEntry({ children }: { children: ReactNode }) {
  return <div style={entryStyle}>{children}</div>;
}

export function ModeToggle({
  mode,
  onChange,
}: {
  mode: DiffMode;
  onChange: (m: DiffMode) => void;
}) {
  return (
    <div
      style={{
        marginLeft: 'auto',
        display: 'flex',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-xs)',
        overflow: 'hidden',
      }}
    >
      {(['unified', 'split'] as const).map((m, i) => (
        <button
          key={m}
          type="button"
          onClick={() => onChange(m)}
          style={{
            padding: '2px 8px',
            fontSize: 11,
            border: 'none',
            borderRight: i === 0 ? '1px solid var(--border-subtle)' : 'none',
            background: mode === m ? 'var(--surface-3, var(--surface-2))' : 'transparent',
            color: mode === m ? 'var(--text-primary, inherit)' : 'var(--text-secondary)',
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          {m === 'unified' ? 'Unified' : 'Split'}
        </button>
      ))}
    </div>
  );
}

export function WorkflowGraphDiff({
  data,
  isLoading,
  error,
  resolveRef,
  mode: modeProp,
  onModeChange,
  settingsDiff = null,
  settingsComparable = false,
  settingsError,
}: {
  data: WorkflowVersionDiff | undefined;
  isLoading: boolean;
  error: unknown;
  resolveRef?: RefResolver;
  mode?: DiffMode;
  onModeChange?: (m: DiffMode) => void;
  /**
   * Workflow-level (non-graph) delta between the same two versions, or `null`
   * when unchanged. Computed by the caller from the version rows, because the
   * diff endpoint only reports nodes and edges. Defaults to `null` so callers
   * that only care about the graph are unaffected.
   */
  settingsDiff?: VersionSettingsDiff | null;
  /**
   * Whether the caller was actually able to compare the workflow-level fields.
   * `false` means "unknown", not "unchanged" — the empty state narrows its
   * claim to the graph rather than asserting the versions are identical.
   * Defaults to `false` so graph-only callers keep the honest narrow wording.
   */
  settingsComparable?: boolean;
  /** Why the workflow-level comparison was unavailable, when it failed outright. */
  settingsError?: unknown;
}) {
  const [internalMode, setInternalMode] = useState<DiffMode>('unified');
  const mode = modeProp ?? internalMode;
  const setMode = onModeChange ?? setInternalMode;
  const isControlled = modeProp !== undefined;

  // `diffLines` is an LCS with a full `(rows+1) x (cols+1)` DP matrix. For the
  // settings section that runs over the whole canonical contract — entire JSON
  // Schemas, not one node's config as before this section existed — so it is by
  // far the most expensive thing here. Keyed on `settingsDiff` alone and not
  // `mode`, because the line list doesn't depend on the render mode: toggling
  // unified/split, or re-pointing the baseline, no longer re-pays it.
  //
  // Must sit above the early returns below to satisfy the rules of hooks.
  const settingsLines = useMemo(
    () => (settingsDiff ? diffLines(settingsDiff.before, settingsDiff.after) : null),
    [settingsDiff],
  );

  if (isLoading && !data) {
    return (
      <div className="t-secondary t-sm" style={{ padding: 'var(--sp-2)' }}>
        Loading changes…
      </div>
    );
  }
  if (error) {
    return (
      <div className="alert alert--danger t-sm" style={{ margin: 0, padding: 'var(--sp-2)' }}>
        Failed to load graph diff: {errorMessage(error)}
      </div>
    );
  }
  if (!data) {
    return (
      <p className="t-secondary t-xs" style={{ margin: 0 }}>
        (no diff)
      </p>
    );
  }

  // Rendered in BOTH paths below. An earlier version lived only inside the
  // empty-diff branch, which meant that as soon as the graph had any change at
  // all the notice vanished — leaving a reviewer unable to tell whether the
  // settings section is absent because nothing changed or because the comparison
  // never ran. That ambiguity is the whole reason `settingsComparable` exists,
  // so the disclosure has to be symmetric.
  const settingsDisclosure = !settingsComparable ? (
    <p className="t-muted t-xs" style={{ margin: 0 }}>
      {settingsError
        ? `Workflow-level schema and dedup settings could not be compared: ${errorMessage(settingsError)}`
        : 'Workflow-level schema and dedup settings were not compared.'}
    </p>
  ) : null;

  if (!hasAnyChanges(data, settingsDiff)) {
    // Only claim "no changes" outright when the workflow-level fields were
    // actually compared. Otherwise scope the statement to the graph — which is
    // all the diff endpoint reported — so an unavailable settings comparison
    // never masquerades as a clean diff.
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
        <p className="t-secondary t-xs" style={{ margin: 0 }}>
          {settingsComparable ? 'No changes' : 'No graph changes'} between v
          {data.from.versionNumber} and v{data.to.versionNumber}.
        </p>
        {settingsDisclosure}
      </div>
    );
  }

  const nodeBadges = nodeCountBadges(data);
  const edgeSummary = edgeSummaryText(data);

  return (
    <div style={containerStyle}>
      {settingsDisclosure}
      <div style={summaryRowStyle}>
        {settingsDiff ? (
          <span
            className="badge badge--neutral t-xs"
            style={summaryBadgeStyle('changed')}
            title={settingsDiff.changedFields.map((f) => settingsFieldLabels[f]).join(', ')}
          >
            ~{settingsDiff.changedFields.length}{' '}
            {settingsDiff.changedFields.length === 1 ? 'setting' : 'settings'}
          </span>
        ) : null}
        {nodeBadges.map((badge) => (
          <span
            key={badge.text}
            className="badge badge--neutral t-xs"
            style={summaryBadgeStyle(badge.kind)}
          >
            {badge.text}
          </span>
        ))}
        {!isControlled ? <ModeToggle mode={mode} onChange={setMode} /> : null}
      </div>

      {edgeSummary ? (
        <div
          className="t-secondary t-xs"
          title={edgeSummary}
          style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-1)' }}
        >
          <span style={{ color: successColor }}>{data.edges.added.length} added</span>
          <span>/</span>
          <span style={{ color: dangerColor }}>{data.edges.removed.length} removed</span>
          <span>/</span>
          <span style={{ color: warningColor }}>{data.edges.changed.length} changed edges</span>
        </div>
      ) : null}

      {settingsDiff && settingsLines ? (
        <section style={sectionStyle}>
          {/* "Settings", not "schema": two of the five fields (dedupEnabled,
              signalKeyTemplate) aren't schemas, so a dedup flip under a
              "Workflow schema" heading would be miscategorised. */}
          <SectionLabel>Workflow settings</SectionLabel>
          {/* One block for the whole contract, not one per field: unchanged
              fields become context lines, so the changes are shown situated in
              the document. Same treatment a changed node gets. */}
          <DiffEntry>{renderBlock(settingsLines, mode)}</DiffEntry>
        </section>
      ) : null}

      {data.nodes.changed.length > 0 ? (
        <section style={sectionStyle}>
          <SectionLabel>Changed nodes</SectionLabel>
          {data.nodes.changed.map((node) => {
            const lines = diffLines(
              nodeSnapshotText(node.before, resolveRef),
              nodeSnapshotText(node.after, resolveRef),
            );
            // The server says these fields differ, but `resolveRef` humanizes
            // id-valued config fields — two distinct ids that share a
            // name/provider/modelId resolve to the SAME label, so the rendered
            // block can come out all-context. Without the field list that reads
            // as a bug ("why is this node listed?"), so bring it back exactly
            // when the block can't speak for itself.
            const hasVisibleChange = lines.some((line) => line.kind !== 'context');
            return (
              <DiffEntry key={node.nodeKey}>
                <div
                  style={headerRowStyle}
                  // Tooltip only when the field list isn't already inline below,
                  // so the same text isn't presented twice.
                  title={hasVisibleChange ? `Changed: ${node.changedFields.join(', ')}` : undefined}
                >
                  <strong className="t-sm t-mono">~ {node.nodeKey}</strong>
                  {!hasVisibleChange ? (
                    <span className="t-secondary t-xs">
                      ({node.changedFields.join(', ')} — differs only by an id that resolves to the
                      same label)
                    </span>
                  ) : null}
                </div>
                {renderBlock(lines, mode)}
              </DiffEntry>
            );
          })}
        </section>
      ) : null}

      {data.nodes.added.length > 0 ? (
        <section style={sectionStyle}>
          <SectionLabel>Added nodes</SectionLabel>
          {data.nodes.added.map((node) => (
            <DiffEntry key={node.nodeKey}>
              <div style={headerRowStyle}>
                <strong className="t-sm t-mono">+ {node.nodeKey}</strong>
                <span className="t-secondary t-xs">[{node.nodeType}]</span>
              </div>
              {renderBlock(snapshotLines(nodeSnapshotText(node, resolveRef), 'add'), mode)}
            </DiffEntry>
          ))}
        </section>
      ) : null}

      {data.nodes.removed.length > 0 ? (
        <section style={sectionStyle}>
          <SectionLabel>Removed nodes</SectionLabel>
          {data.nodes.removed.map((node) => (
            <DiffEntry key={node.nodeKey}>
              <div style={headerRowStyle}>
                <strong className="t-sm t-mono">− {node.nodeKey}</strong>
              </div>
              {renderBlock(snapshotLines(nodeSnapshotText(node, resolveRef), 'del'), mode)}
            </DiffEntry>
          ))}
        </section>
      ) : null}

      {data.edges.added.length > 0 ||
      data.edges.removed.length > 0 ||
      data.edges.changed.length > 0 ? (
        <section style={sectionStyle}>
          <SectionLabel>Edges</SectionLabel>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
            {data.edges.added.map((edge) => (
              <div
                key={`add-${edge.fromNodeKey}-${edge.toNodeKey}`}
                className="t-xs t-secondary"
                style={headerRowStyle}
              >
                <strong style={{ color: successColor }}>
                  + {edge.fromNodeKey} → {edge.toNodeKey}
                </strong>
              </div>
            ))}
            {data.edges.removed.map((edge) => (
              <div
                key={`del-${edge.fromNodeKey}-${edge.toNodeKey}`}
                className="t-xs t-secondary"
                style={headerRowStyle}
              >
                <strong style={{ color: dangerColor }}>
                  − {edge.fromNodeKey} → {edge.toNodeKey}
                </strong>
              </div>
            ))}
            {data.edges.changed.map((edge) => (
              <div
                key={`chg-${edge.fromNodeKey}-${edge.toNodeKey}`}
                className="t-xs t-secondary"
                style={headerRowStyle}
              >
                <strong style={{ color: warningColor }}>
                  ~ {edge.fromNodeKey} → {edge.toNodeKey}
                </strong>
                <span>({edge.changedFields.join(', ')})</span>
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}
